/**
 * Background Sync Service
 * 
 * Handles automatic synchronization of critical data in the background.
 * Provides conflict resolution and ensures data freshness.
 */

import CacheManager from '../utils/CacheManager';
import RetryManager from '../utils/RetryManager';
import type { RetryOptions } from '../utils/RetryManager';

export interface SyncConfig {
  interval: number; // Sync interval in milliseconds
  retryOptions?: RetryOptions;
  priority: 'low' | 'medium' | 'high';
  conflictResolution: 'server' | 'client' | 'merge';
}

export interface SyncTask {
  id: string;
  type: 'player' | 'market' | 'chunks' | 'custom';
  key: string;
  config: SyncConfig;
  lastSync: number;
  isRunning: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  customSync?: () => Promise<any>;
}

export interface ExtendedSyncConfig extends SyncConfig {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export interface SyncStats {
  totalTasks: number;
  runningTasks: number;
  successfulSyncs: number;
  failedSyncs: number;
  lastSyncTime: number;
  averageSyncTime: number;
}

class BackgroundSyncService {
  private static instance: BackgroundSyncService;
  private tasks: Map<string, SyncTask> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private isOnline = navigator.onLine;
  private isPaused = false;
  
  // Statistics
  private stats: SyncStats = {
    totalTasks: 0,
    runningTasks: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    lastSyncTime: 0,
    averageSyncTime: 0,
  };

  private constructor() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  public static getInstance(): BackgroundSyncService {
    if (!BackgroundSyncService.instance) {
      BackgroundSyncService.instance = new BackgroundSyncService();
    }
    return BackgroundSyncService.instance;
  }

  /**
   * Register a new sync task
   */
  public registerTask(task: Omit<SyncTask, 'lastSync' | 'isRunning'>): void {
    const fullTask: SyncTask = {
      ...task,
      lastSync: 0,
      isRunning: false,
    };

    this.tasks.set(task.id, fullTask);
    this.stats.totalTasks = this.tasks.size;

    // Start sync interval if not paused and online
    if (!this.isPaused && this.isOnline) {
      this.startSyncInterval(task.id);
    }
  }

  /**
   * Unregister a sync task
   */
  public unregisterTask(taskId: string): void {
    this.tasks.delete(taskId);
    this.stopSyncInterval(taskId);
    this.stats.totalTasks = this.tasks.size;
  }

  /**
   * Start background sync for all tasks
   */
  public start(): void {
    this.isPaused = false;
    
    if (this.isOnline) {
      this.tasks.forEach((task, taskId) => {
        this.startSyncInterval(taskId);
      });
    }
  }

  /**
   * Pause background sync
   */
  public pause(): void {
    this.isPaused = true;
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
  }

  /**
   * Force sync a specific task
   */
  public async forceSync(taskId: string): Promise<boolean> {
    const task = this.tasks.get(taskId);
    if (!task) {
      console.error(`Sync task ${taskId} not found`);
      return false;
    }

    return this.executeSync(task);
  }

  /**
   * Get sync statistics
   */
  public getStats(): SyncStats {
    return { ...this.stats };
  }

  /**
   * Get all registered tasks
   */
  public getTasks(): SyncTask[] {
    return Array.from(this.tasks.values());
  }

  /**
   * Clear all tasks and stop sync
   */
  public clear(): void {
    this.pause();
    this.tasks.clear();
    this.stats = {
      totalTasks: 0,
      runningTasks: 0,
      successfulSyncs: 0,
      failedSyncs: 0,
      lastSyncTime: 0,
      averageSyncTime: 0,
    };
  }

  /**
   * Destroy the service and clean up
   */
  public destroy(): void {
    this.clear();
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  // Private methods

  private startSyncInterval(taskId: string): void {
    const task = this.tasks.get(taskId);
    if (!task) return;

    // Clear existing interval
    this.stopSyncInterval(taskId);

    // Set new interval
    const interval = setInterval(() => {
      if (!task.isRunning && this.isOnline && !this.isPaused) {
        this.executeSync(task);
      }
    }, task.config.interval);

    this.intervals.set(taskId, interval);
  }

  private stopSyncInterval(taskId: string): void {
    const interval = this.intervals.get(taskId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(taskId);
    }
  }

  private async executeSync(task: SyncTask): Promise<boolean> {
    if (task.isRunning) return false;

    task.isRunning = true;
    this.stats.runningTasks++;

    const startTime = Date.now();

    try {
      let data: any;
      let success = false;

      switch (task.type) {
        case 'player':
          success = await this.syncPlayerData(task);
          break;
        case 'market':
          success = await this.syncMarketData(task);
          break;
        case 'chunks':
          success = await this.syncChunkData(task);
          break;
        case 'custom':
          if (task.customSync) {
            const result = await RetryManager.execute(
              task.customSync,
              task.config.retryOptions
            );
            if (result.data) {
              data = result.data;
              success = true;
            }
          }
          break;
      }

      if (success) {
        task.lastSync = Date.now();
        this.stats.successfulSyncs++;
        task.onSuccess?.(data);
      } else {
        this.stats.failedSyncs++;
        task.onError?.(new Error('Sync failed'));
      }

      const duration = Date.now() - startTime;
      this.updateAverageSyncTime(duration);
      this.stats.lastSyncTime = Date.now();

      return success;
    } catch (error) {
      console.error(`Sync task ${task.id} failed:`, error);
      this.stats.failedSyncs++;
      task.onError?.(error);
      return false;
    } finally {
      task.isRunning = false;
      this.stats.runningTasks--;
    }
  }

  private async syncPlayerData(task: SyncTask): Promise<boolean> {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    const result = await RetryManager.execute(async () => {
      const response = await fetch(`${apiUrl}/api/player/${task.key}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const playerData = await response.json();
      const serverTimestamp = response.headers.get('X-Timestamp') 
        ? parseInt(response.headers.get('X-Timestamp')!)
        : Date.now();
      
      // Handle conflict resolution
      const cachedData = CacheManager.getPlayerData(task.key);
      if (cachedData && serverTimestamp < (cachedData as any).lastModified) {
        if (task.config.conflictResolution === 'client') {
          return cachedData; // Keep client version
        } else if (task.config.conflictResolution === 'merge') {
          // Simple merge - in real implementation, this would be more sophisticated
          return { ...playerData, ...cachedData };
        }
      }
      
      CacheManager.setPlayerData(task.key, playerData, {
        serverTimestamp,
      });
      
      return playerData;
    }, task.config.retryOptions);

    return !!result.data;
  }

  private async syncMarketData(task: SyncTask): Promise<boolean> {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    const result = await RetryManager.execute(async () => {
      const response = await fetch(`${apiUrl}/api/market/${task.key}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const marketData = await response.json();
      const serverTimestamp = response.headers.get('X-Timestamp') 
        ? parseInt(response.headers.get('X-Timestamp')!)
        : Date.now();
      
      CacheManager.setMarketData(task.key, marketData, {
        serverTimestamp,
      });
      
      return marketData;
    }, task.config.retryOptions);

    return !!result.data;
  }

  private async syncChunkData(task: SyncTask): Promise<boolean> {
    // For chunks, we only sync if they're stale
    const versionInfo = CacheManager.getVersionInfo('chunks', task.key);
    if (versionInfo && Date.now() - versionInfo.timestamp < 300000) {
      return true; // Not stale, no sync needed
    }

    const [x, y] = task.key.split('_').map(Number);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    const result = await RetryManager.execute(async () => {
      const response = await fetch(`${apiUrl}/api/world/chunk/${x}/${y}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const chunkData = await response.json();
      const serverTimestamp = response.headers.get('X-Timestamp') 
        ? parseInt(response.headers.get('X-Timestamp')!)
        : Date.now();
      
      CacheManager.setChunk(task.key, chunkData, {
        serverTimestamp,
      });
      
      return chunkData;
    }, task.config.retryOptions);

    return !!result.data;
  }

  private updateAverageSyncTime(duration: number): void {
    const totalSyncs = this.stats.successfulSyncs + this.stats.failedSyncs;
    if (totalSyncs === 1) {
      this.stats.averageSyncTime = duration;
    } else {
      this.stats.averageSyncTime = 
        (this.stats.averageSyncTime * (totalSyncs - 1) + duration) / totalSyncs;
    }
  }

  private handleOnline(): void {
    this.isOnline = true;
    if (!this.isPaused) {
      this.start();
    }
  }

  private handleOffline(): void {
    this.isOnline = false;
    this.pause();
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      // Page is hidden, reduce sync frequency
      this.pause();
    } else {
      // Page is visible, resume normal sync
      this.start();
    }
  }
}

// Export singleton instance
export default BackgroundSyncService.getInstance();

// Helper functions for common sync tasks
export const createPlayerSyncTask = (
  characterId: string,
  options: Partial<SyncConfig> = {}
): Omit<SyncTask, 'lastSync' | 'isRunning'> => ({
  id: `player_${characterId}`,
  type: 'player',
  key: `player_${characterId}`,
  config: {
    interval: 60000, // 1 minute
    priority: 'high',
    conflictResolution: 'server',
    retryOptions: RetryManager.createCriticalDataRetryOptions(),
    ...options,
  },
});

export const createMarketSyncTask = (
  marketId: string = 'default',
  options: Partial<SyncConfig> = {}
): Omit<SyncTask, 'lastSync' | 'isRunning'> => ({
  id: `market_${marketId}`,
  type: 'market',
  key: `market_${marketId}`,
  config: {
    interval: 300000, // 5 minutes
    priority: 'medium',
    conflictResolution: 'server',
    retryOptions: RetryManager.createCacheRetryOptions(),
    ...options,
  },
});

export const createChunkSyncTask = (
  chunkId: string,
  options: Partial<SyncConfig> = {}
): Omit<SyncTask, 'lastSync' | 'isRunning'> => ({
  id: `chunk_${chunkId}`,
  type: 'chunks',
  key: chunkId,
  config: {
    interval: 600000, // 10 minutes
    priority: 'low',
    conflictResolution: 'server',
    retryOptions: RetryManager.createCacheRetryOptions(),
    ...options,
  },
});
